/**
 * Sequences Service (DAG-based)
 * CRUD operations for message sequences, DAG steps, and enrollments
 */

import { supabase } from "./supabase";
import type {
  MessageSequence,
  SequenceStep,
  SequenceEnrollment,
  ScheduledMessage,
  SequenceMessageHistory,
  SequenceAnalytics,
  SequenceWithAnalytics,
  CreateSequenceInput,
  UpdateSequenceInput,
  CreateSequenceStepInput,
  UpdateSequenceStepInput,
  EnrollContactInput,
  BulkEnrollInput,
} from "../types/sequence.types";
import type { UUID } from "../types/common.types";

// ========================================
// SEQUENCE OPERATIONS
// ========================================

export async function getSequences(): Promise<MessageSequence[]> {
  const { data, error } = await supabase
    .from("message_sequences")
    .select(
      `
      *,
      list:lists(id, name, contact_count),
      campaign:campaigns(id, name),
      event:events(id, title, start_date)
    `,
    )
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching sequences:", error);
    throw new Error(`Failed to fetch sequences: ${error.message}`);
  }

  return data as MessageSequence[];
}

/**
 * Active sequences only — the sequence-picker for the manual "Start a sequence"
 * action on the contact record. Only status='active' sequences accept manual
 * enrollment (the enroll RPC rejects any non-active sequence), so we never offer
 * a draft/paused/archived one in the picker.
 */
export async function getActiveSequences(): Promise<MessageSequence[]> {
  const all = await getSequences();
  return all.filter((s) => s.status === "active");
}

export async function getSequencesWithAnalytics(): Promise<
  SequenceWithAnalytics[]
> {
  const sequences = await getSequences();

  const { data: enrollmentCounts, error: countError } = await supabase
    .from("sequence_enrollments")
    .select("sequence_id")
    .eq("status", "active");

  if (countError) {
    console.error("Error fetching enrollment counts:", countError);
  }

  const countMap = new Map<string, number>();
  enrollmentCounts?.forEach((e) => {
    countMap.set(e.sequence_id, (countMap.get(e.sequence_id) || 0) + 1);
  });

  return sequences.map((s) => ({
    ...s,
    active_enrollments: countMap.get(s.id) || 0,
  }));
}

export async function getSequence(
  id: UUID,
): Promise<SequenceWithAnalytics | null> {
  const { data, error } = await supabase
    .from("message_sequences")
    .select(
      `
      *,
      list:lists(id, name, contact_count),
      campaign:campaigns(id, name),
      event:events(id, title, start_date)
    `,
    )
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    console.error("Error fetching sequence:", error);
    throw new Error(`Failed to fetch sequence: ${error.message}`);
  }

  const steps = await getSequenceStepTree(id);
  const analytics = await getSequenceAnalytics(id);

  return {
    ...data,
    steps,
    analytics,
  } as SequenceWithAnalytics;
}

export async function createSequence(
  input: CreateSequenceInput,
): Promise<MessageSequence> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from("message_sequences")
    .insert([
      {
        ...input,
        created_by: user?.id,
        updated_by: user?.id,
      },
    ])
    .select()
    .single();

  if (error) {
    console.error("Error creating sequence:", error);
    throw new Error(`Failed to create sequence: ${error.message}`);
  }

  return data as MessageSequence;
}

export async function updateSequence(
  id: UUID,
  input: UpdateSequenceInput,
  // Optimistic concurrency (external audit Medium, made MANDATORY in round
  // 3): the updated_at the caller last read. The DB trigger bumps
  // updated_at on every UPDATE, so a mismatch means someone else saved
  // since — refuse instead of silently overwriting their change (the
  // trigger_type lost-update class). Every browser-originated write —
  // including the status helpers below — must carry the version it acted
  // on.
  expectedUpdatedAt: string,
): Promise<MessageSequence> {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data, error } = await supabase
    .from("message_sequences")
    .update({
      ...input,
      updated_by: user?.id,
    })
    .eq("id", id)
    .eq("updated_at", expectedUpdatedAt)
    .select()
    .maybeSingle();

  if (error) {
    console.error("Error updating sequence:", error);
    throw new Error(`Failed to update sequence: ${error.message}`);
  }
  if (!data) {
    throw new Error(
      "This sequence was changed by someone else since you opened it. Reload the page and re-apply your edits.",
    );
  }

  return data as MessageSequence;
}

export async function deleteSequence(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("message_sequences")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting sequence:", error);
    throw new Error(`Failed to delete sequence: ${error.message}`);
  }
}

// Status helpers carry the concurrency token too (re-audit round 3 Medium):
// a stale page could otherwise activate a sequence whose configuration
// changed since the operator reviewed it, starting real sends without any
// conflict warning.
export async function activateSequence(
  id: UUID,
  expectedUpdatedAt: string,
): Promise<MessageSequence> {
  return updateSequence(id, { status: "active" }, expectedUpdatedAt);
}

export async function pauseSequence(
  id: UUID,
  expectedUpdatedAt: string,
): Promise<MessageSequence> {
  return updateSequence(id, { status: "paused" }, expectedUpdatedAt);
}

export async function archiveSequence(
  id: UUID,
  expectedUpdatedAt: string,
): Promise<MessageSequence> {
  return updateSequence(id, { status: "archived" }, expectedUpdatedAt);
}

// ========================================
// STEP OPERATIONS (DAG)
// ========================================

/**
 * Get the full step tree via the recursive CTE function,
 * then build the nested tree client-side.
 */
export async function getSequenceStepTree(
  sequenceId: UUID,
): Promise<SequenceStep[]> {
  const { data, error } = await supabase.rpc("get_sequence_step_tree", {
    p_sequence_id: sequenceId,
  });

  if (error) {
    console.error("Error fetching step tree:", error);
    throw new Error(`Failed to fetch step tree: ${error.message}`);
  }

  if (!data || data.length === 0) {
    return [];
  }

  return buildStepTree(data as SequenceStep[]);
}

/**
 * Build nested tree from flat list (ordered by depth, branch_order)
 */
function buildStepTree(flatSteps: SequenceStep[]): SequenceStep[] {
  const stepMap = new Map<string, SequenceStep>();
  const roots: SequenceStep[] = [];

  // First pass: index all steps
  for (const step of flatSteps) {
    stepMap.set(step.id, { ...step, children: [] });
  }

  // Second pass: build tree
  for (const step of flatSteps) {
    const node = stepMap.get(step.id)!;
    if (step.parent_step_id === null) {
      roots.push(node);
    } else {
      const parent = stepMap.get(step.parent_step_id);
      if (parent) {
        parent.children!.push(node);
      }
    }
  }

  return roots;
}

/**
 * Get flat list of steps (no tree building) - useful for lookups
 */
export async function getSequenceStepsFlat(
  sequenceId: UUID,
): Promise<SequenceStep[]> {
  const { data, error } = await supabase
    .from("message_sequence_steps")
    .select("*")
    .eq("sequence_id", sequenceId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error fetching sequence steps:", error);
    throw new Error(`Failed to fetch sequence steps: ${error.message}`);
  }

  return data as SequenceStep[];
}

export async function createSequenceStep(
  input: CreateSequenceStepInput,
): Promise<SequenceStep> {
  const { data, error } = await supabase
    .from("message_sequence_steps")
    .insert([input])
    .select()
    .single();

  if (error) {
    console.error("Error creating sequence step:", error);
    throw new Error(`Failed to create sequence step: ${error.message}`);
  }

  return data as SequenceStep;
}

export async function updateSequenceStep(
  id: UUID,
  input: UpdateSequenceStepInput,
): Promise<SequenceStep> {
  const { data, error } = await supabase
    .from("message_sequence_steps")
    .update(input)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating sequence step:", error);
    throw new Error(`Failed to update sequence step: ${error.message}`);
  }

  return data as SequenceStep;
}

/**
 * Renumber a set of sibling branch steps so their branch_order matches the
 * given evaluation order (0..n-1).
 *
 * Only rows whose stored order already differs from the target are written —
 * see branchOrderWrites. Writes are sequential so a failure leaves a
 * prefix applied rather than an interleaved mess, and the caller can retry
 * (the operation is idempotent).
 *
 * The worker skips 'default' children when walking branch_order, so callers
 * should pass only the CONDITIONAL siblings.
 */
export async function reorderBranchSiblings(
  writes: Array<{ id: UUID; branch_order: number }>,
): Promise<void> {
  for (const w of writes) {
    await updateSequenceStep(w.id, { branch_order: w.branch_order });
  }
}

// deleteSequenceStep (plain table DELETE, cascades the subtree) was removed 2026-11-04 (external audit,
// stale-leaf data loss). Journey steps are deleted ONLY via deleteSequenceStepChecked (mig 1721).

/**
 * Server answer of delete_sequence_step_keep_children (mig 1720).
 * `allowed=false` comes back only from a dry run; a real call raises instead.
 */
export interface StepDeleteKeepChildrenPlan {
  allowed: boolean;
  /** Why it is refused (dry run only), e.g. FIRST_STEP_IS_A_SPLIT. */
  code?: string;
  /** Operator-language explanation of the refusal. */
  message?: string;
  mode?:
    | "no_children"
    | "child_replaces_step"
    | "conditional_child_moves_up"
    | "split_moves_up";
  children?: number;
  people_at_step?: number;
  warnings?: string[];
  dry_run?: boolean;
}

/**
 * Delete ONE step and keep the steps under it (re-parented to the deleted
 * step's parent, conditions and order preserved). The server refuses any shape
 * where that would change who goes where. `dryRun` asks the question without
 * writing. Authorization is the caller's own step-edit rights (RLS).
 */
export async function deleteSequenceStepKeepChildren(
  id: UUID,
  dryRun = false,
): Promise<StepDeleteKeepChildrenPlan> {
  const { data, error } = await supabase.rpc(
    "delete_sequence_step_keep_children",
    { p_step_id: id, p_dry_run: dryRun },
  );
  if (error) {
    // Strip the machine prefix ("STEP_DELETE_REFUSED:CODE: ") for display.
    const msg = (error.message || "").replace(
      /^(?:STEP_DELETE_[A-Z_]+|STEP_NOT_FOUND):(?:[A-Z_]+:)?\s*/,
      "",
    );
    throw new Error(msg || "Could not delete the step.");
  }
  return data as StepDeleteKeepChildrenPlan;
}

/**
 * The delete the journey UI uses (mig 1721), for both choices. `seenIds` are the
 * steps the user was shown under the step (empty for a leaf); the server locks the
 * journey and refuses with "The steps under this step changed ..." if the subtree
 * is no longer exactly that, so nothing the user did not see is ever deleted.
 */
export async function deleteSequenceStepChecked(
  id: UUID,
  keepChildren: boolean,
  seenIds: string[],
): Promise<StepDeleteKeepChildrenPlan> {
  const { data, error } = await supabase.rpc("delete_sequence_step_checked", {
    p_step_id: id,
    p_keep_children: keepChildren,
    p_expected_descendant_ids: seenIds,
  });
  if (error) {
    const msg = (error.message || "").replace(
      /^(?:STEP_DELETE_[A-Z_]+|STEP_NOT_FOUND):(?:[A-Z_]+:)?\s*/,
      "",
    );
    throw new Error(msg || "Could not delete the step.");
  }
  return data as StepDeleteKeepChildrenPlan;
}

// ========================================
// ENROLLMENT OPERATIONS
// ========================================

/**
 * Every enrollment for a sequence, paged.
 *
 * getSequenceEnrollments() issues ONE PostgREST request, which caps at 1000 rows by default — the
 * Enrollments tab showed "1000" for a sequence with 1,465 enrollments. An export built on that
 * would hand an operator a short CSV that looks complete. This pages until exhausted and throws
 * rather than returning a partial list, because the caller uses it to answer "who was contacted?".
 */
export async function fetchAllEnrollmentsForExport(sequenceId: UUID): Promise<
  Array<{
    contact_id: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    status: string | null;
    enrolled_at: string | null;
    completed_at: string | null;
    trigger_source: string | null;
  }>
> {
  const PAGE = 1000;
  const out: Array<Record<string, unknown>> = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("sequence_enrollments")
      .select(
        "contact_id, status, enrolled_at, completed_at, trigger_source, contact:contacts(first_name, last_name, email)",
      )
      .eq("sequence_id", sequenceId)
      .order("enrolled_at", { ascending: true })
      .range(from, from + PAGE - 1);

    if (error) {
      // Fail loudly. A caught-and-ignored page would silently shorten the file.
      throw new Error(
        `Enrollment export failed at row ${from}: ${error.message}. No file was produced.`,
      );
    }
    const page = data ?? [];
    out.push(...(page as Array<Record<string, unknown>>));
    if (page.length < PAGE) {
      break;
    }
  }

  return out.map((r) => {
    const c = (r.contact ?? {}) as Record<string, string | null>;
    return {
      contact_id: (r.contact_id as string) ?? null,
      first_name: c.first_name ?? null,
      last_name: c.last_name ?? null,
      email: c.email ?? null,
      status: (r.status as string) ?? null,
      enrolled_at: (r.enrolled_at as string) ?? null,
      completed_at: (r.completed_at as string) ?? null,
      trigger_source: (r.trigger_source as string) ?? null,
    };
  });
}

export async function getSequenceEnrollments(
  sequenceId: UUID,
  status?: string,
): Promise<SequenceEnrollment[]> {
  let query = supabase
    .from("sequence_enrollments")
    .select(
      `
      *,
      contact:contacts(id, first_name, last_name, email, phone_number),
      current_step:message_sequence_steps(id, name, step_type)
    `,
    )
    .eq("sequence_id", sequenceId)
    .order("enrolled_at", { ascending: false });

  if (status) {
    query = query.eq("status", status);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching enrollments:", error);
    throw new Error(`Failed to fetch enrollments: ${error.message}`);
  }

  return data as SequenceEnrollment[];
}

export async function getContactEnrollments(
  contactId: UUID,
): Promise<SequenceEnrollment[]> {
  const { data, error } = await supabase
    .from("sequence_enrollments")
    .select(
      `
      *,
      sequence:message_sequences(id, name, status)
    `,
    )
    .eq("contact_id", contactId)
    .order("enrolled_at", { ascending: false });

  if (error) {
    console.error("Error fetching contact enrollments:", error);
    throw new Error(`Failed to fetch contact enrollments: ${error.message}`);
  }

  return data as SequenceEnrollment[];
}

// Discriminated outcome from the role-gated `enroll-contact-in-sequence` edge
// function. Every HANDLED outcome (including the RPC's guard refusals) comes back
// as HTTP 200 with one of these statuses, so callers read a single shape rather
// than catching 500s. Must stay in sync with
// supabase/functions/enroll-contact-in-sequence/logic.ts EnrollStatus.
export type ManualEnrollStatus =
  | "enrolled"
  | "already_enrolled"
  | "not_active"
  | "reenrollment_blocked"
  | "no_root_step"
  | "missing_field"
  | "routing_conflict"
  | "failed"
  | "invalid";

export interface ManualEnrollOutcome {
  status: ManualEnrollStatus;
  message: string;
  enrollment_id?: string;
}

/**
 * Manually enroll ONE contact into an existing ACTIVE sequence from the contact
 * record. The authoritative enroll RPC is service_role-only, so this goes through
 * the role-gated `enroll-contact-in-sequence` edge function (which owns the JWT +
 * role gate, calls the RPC with trigger_source='manual' / trigger_type='manual',
 * and maps the RPC's guard failures to clean messages). Returns the discriminated
 * outcome WITHOUT throwing on guard refusals (already-enrolled, not-active,
 * re-enrollment blocked) — only network/unknown errors surface as a 'failed'
 * outcome so the UI can always show an actionable message.
 */
export async function enrollContactInSequence(
  contactId: UUID,
  sequenceId: UUID,
): Promise<ManualEnrollOutcome> {
  const { data, error } = await supabase.functions.invoke(
    "enroll-contact-in-sequence",
    { body: { contact_id: contactId, sequence_id: sequenceId } },
  );

  if (error || !data) {
    console.error("Error enrolling contact in sequence:", error);
    return {
      status: "failed",
      message:
        "We couldn't reach the server to start the sequence. Please try again.",
    };
  }

  return data as ManualEnrollOutcome;
}

// enrollContact previously threw (external audit High, 2026-07-19): the direct
// browser RPC path was removed. It now delegates to the role-gated edge function
// via enrollContactInSequence and throws on any non-enrolled outcome so the
// bulk helper below keeps its error-collecting contract.
export async function enrollContact(
  input: EnrollContactInput,
): Promise<SequenceEnrollment> {
  const outcome = await enrollContactInSequence(
    input.contact_id,
    input.sequence_id,
  );
  if (outcome.status !== "enrolled") {
    throw new Error(outcome.message);
  }
  // The edge function returns only the new enrollment id; callers that need the
  // full row can re-fetch. Return a minimal shape carrying the id.
  return { id: outcome.enrollment_id } as unknown as SequenceEnrollment;
}

export async function bulkEnrollContacts(
  input: BulkEnrollInput,
): Promise<{ enrolled: number; errors: string[] }> {
  const results = { enrolled: 0, errors: [] as string[] };

  for (const contactId of input.contact_ids) {
    try {
      await enrollContact({
        sequence_id: input.sequence_id,
        contact_id: contactId,
        trigger_source: input.trigger_source || "bulk_manual",
      });
      results.enrolled++;
    } catch (error) {
      results.errors.push(
        `Contact ${contactId}: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  return results;
}

export async function cancelEnrollment(id: UUID): Promise<SequenceEnrollment> {
  const { data, error } = await supabase
    .from("sequence_enrollments")
    .update({
      status: "cancelled",
      cancelled_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error cancelling enrollment:", error);
    throw new Error(`Failed to cancel enrollment: ${error.message}`);
  }

  return data as SequenceEnrollment;
}

export async function pauseEnrollment(id: UUID): Promise<SequenceEnrollment> {
  const { data, error } = await supabase
    .from("sequence_enrollments")
    .update({ status: "paused" })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error pausing enrollment:", error);
    throw new Error(`Failed to pause enrollment: ${error.message}`);
  }

  return data as SequenceEnrollment;
}

export async function resumeEnrollment(id: UUID): Promise<SequenceEnrollment> {
  const { data, error } = await supabase
    .from("sequence_enrollments")
    .update({ status: "active" })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error resuming enrollment:", error);
    throw new Error(`Failed to resume enrollment: ${error.message}`);
  }

  return data as SequenceEnrollment;
}

// ========================================
// SCHEDULED MESSAGES
// ========================================

export async function getScheduledMessages(
  sequenceId?: UUID,
  status?: string,
): Promise<ScheduledMessage[]> {
  let query = supabase
    .from("scheduled_messages")
    .select(
      `
      *,
      contact:contacts(id, first_name, last_name, email, phone_number)
    `,
    )
    .eq("source_type", "sequence")
    .order("scheduled_for", { ascending: true });

  if (sequenceId) {
    query = query.in(
      "enrollment_id",
      supabase
        .from("sequence_enrollments")
        .select("id")
        .eq("sequence_id", sequenceId),
    );
  }

  if (status) {
    query = query.eq("status", status);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching scheduled messages:", error);
    throw new Error(`Failed to fetch scheduled messages: ${error.message}`);
  }

  return data as ScheduledMessage[];
}

export async function cancelScheduledMessage(id: UUID): Promise<void> {
  const { error } = await supabase
    .from("scheduled_messages")
    .update({ status: "cancelled" })
    .eq("id", id)
    .eq("status", "pending");

  if (error) {
    console.error("Error cancelling scheduled message:", error);
    throw new Error(`Failed to cancel scheduled message: ${error.message}`);
  }
}

// ========================================
// MESSAGE HISTORY
// ========================================

export async function getEnrollmentHistory(
  enrollmentId: UUID,
): Promise<SequenceMessageHistory[]> {
  const { data, error } = await supabase
    .from("sequence_message_history")
    .select("*")
    .eq("enrollment_id", enrollmentId)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error fetching enrollment history:", error);
    throw new Error(`Failed to fetch enrollment history: ${error.message}`);
  }

  return data as SequenceMessageHistory[];
}

// ========================================
// ANALYTICS
// ========================================

export interface StepCount {
  step_id: string;
  active_count: number;
  completed_count: number;
  total_passed: number;
}

export async function getSequenceStepCounts(
  sequenceId: UUID,
): Promise<Map<string, StepCount>> {
  const { data, error } = await supabase.rpc("get_sequence_step_counts", {
    p_sequence_id: sequenceId,
  });

  if (error) {
    console.error("Error fetching step counts:", error);
    return new Map();
  }

  const map = new Map<string, StepCount>();
  for (const row of data || []) {
    map.set(row.step_id, {
      step_id: row.step_id,
      active_count: row.active_count,
      completed_count: row.completed_count,
      total_passed: row.total_passed,
    });
  }
  return map;
}

export async function getSequenceAnalytics(
  sequenceId: UUID,
): Promise<SequenceAnalytics> {
  const { data, error } = await supabase.rpc("get_sequence_analytics", {
    p_sequence_id: sequenceId,
  });

  if (error) {
    console.error("Error fetching sequence analytics:", error);
    return {
      total_enrolled: 0,
      active_count: 0,
      completed_count: 0,
      cancelled_count: 0,
      messages_sent: 0,
      messages_delivered: 0,
      messages_failed: 0,
      emails_opened: 0,
      emails_clicked: 0,
      emails_sent: 0,
    };
  }

  return (data?.[0] || {
    total_enrolled: 0,
    active_count: 0,
    completed_count: 0,
    cancelled_count: 0,
    messages_sent: 0,
    messages_delivered: 0,
    messages_failed: 0,
    emails_opened: 0,
    emails_clicked: 0,
    emails_sent: 0,
  }) as SequenceAnalytics;
}

// ========================================
// UTILITY FUNCTIONS
// ========================================

export function formatDelay(value: number, unit: string): string {
  if (value === 0) {
    return "Immediately";
  }
  if (value === 1) {
    return `1 ${unit.slice(0, -1)}`;
  }
  return `${value} ${unit}`;
}

export function calculateScheduledTime(
  baseTime: Date,
  delayValue: number,
  delayUnit: string,
): Date {
  const result = new Date(baseTime);

  switch (delayUnit) {
    case "minutes":
      result.setMinutes(result.getMinutes() + delayValue);
      break;
    case "hours":
      result.setHours(result.getHours() + delayValue);
      break;
    case "days":
      result.setDate(result.getDate() + delayValue);
      break;
    case "weeks":
      result.setDate(result.getDate() + delayValue * 7);
      break;
  }

  return result;
}
